import { Badge, Box, Flex, HStack, Icon, Menu, Portal, Separator, Text, VStack } from "@chakra-ui/react";
import {
    HiChevronDown,
    HiEllipsisHorizontal,
    HiOutlineCalendarDays,
    HiOutlineIdentification,
    HiOutlineUsers,
} from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import CompanyMembersTable from "./CompanyMembersTable";
import { companyLabel } from "../companyLabel";

const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pl-PL");
};

const Meta = ({ icon, children }) => (
    <HStack gap={1.5} color="text.grey" fontSize="sm" flexShrink={0}>
        <Icon fontSize={15}>{icon}</Icon>
        <Text>{children}</Text>
    </HStack>
);

const CompanyRow = ({
    company,
    expanded,
    onToggleExpand,
    onEdit,
    onToggleActive,
    onEditMember,
    onToggleMember,
    onResendInvitation,
    onAddMember,
}) => {
    const navigate = useNavigate();
    const isActive = company.status === "Active";
    const toggle = () => onToggleExpand(company);

    return (
        <Box
            w="full"
            bg="bg.secondary"
            borderRadius={12}
            borderWidth="1px"
            borderColor={isActive ? "border.primary" : "gray.700"}
            overflow="hidden"
        >
            <Flex
                align="center"
                gap={3}
                px={5}
                py={4}
                cursor="pointer"
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={toggle}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle();
                    }
                }}
                transition="background 0.15s"
                _hover={{ bg: "bg.highlight" }}
            >
                <VStack align="flex-start" gap={2} flex="1" minW={0}>
                    <HStack gap={3} flexWrap="wrap">
                        <Text
                            color={isActive ? "white" : "gray.400"}
                            fontWeight="semibold"
                            fontSize="md"
                            lineClamp={2}
                            wordBreak="break-word"
                        >
                            {companyLabel(company)}
                        </Text>
                        <Badge colorPalette={isActive ? "green" : "red"} variant="solid">
                            {isActive ? "Active" : "Inactive"}
                        </Badge>
                    </HStack>
                    <HStack
                        gap={3}
                        rowGap={1}
                        flexWrap="wrap"
                        separator={<Separator orientation="vertical" height="3" borderColor="border.primary" />}
                    >
                        <Meta icon={<HiOutlineIdentification />}>
                            {company.companyNip || "No NIP"}
                        </Meta>
                        <Meta icon={<HiOutlineUsers />}>
                            {company.usersCount} {company.usersCount === 1 ? "user" : "users"}
                        </Meta>
                        <Meta icon={<HiOutlineCalendarDays />}>
                            {formatDate(company.createdAt)}
                        </Meta>
                    </HStack>
                </VStack>

                <HStack gap={1} flexShrink={0} onClick={(e) => e.stopPropagation()}>
                    <Menu.Root positioning={{ placement: "bottom-end" }}>
                        <Menu.Trigger asChild>
                            <Box
                                as="button"
                                aria-label="Company actions"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                boxSize="32px"
                                borderRadius={8}
                                color="text.grey"
                                _hover={{ bg: "bg.inputActive", color: "white" }}
                            >
                                <Icon fontSize={20}><HiEllipsisHorizontal /></Icon>
                            </Box>
                        </Menu.Trigger>
                        <Portal>
                            <Menu.Positioner>
                                <Menu.Content bg="bg.tab" borderColor="border.primary" borderWidth={1} minW="44">
                                    <Menu.Item value="edit" onClick={() => onEdit(company)} color="gray.200" _hover={{ bg: "bg.dark" }}>
                                        Edit
                                    </Menu.Item>
                                    <Menu.Item
                                        value="toggle"
                                        onClick={() => onToggleActive(company)}
                                        color={isActive ? "red.300" : "green.300"}
                                        _hover={{ bg: "bg.dark" }}
                                    >
                                        {isActive ? "Deactivate" : "Activate"}
                                    </Menu.Item>
                                    <Menu.Item value="details" onClick={() => navigate(`/clients/${company.id}`)} color="gray.200" _hover={{ bg: "bg.dark" }}>
                                        Details
                                    </Menu.Item>
                                </Menu.Content>
                            </Menu.Positioner>
                        </Portal>
                    </Menu.Root>
                    <Icon
                        fontSize={22}
                        color="text.grey"
                        transform={expanded ? "rotate(180deg)" : "rotate(0deg)"}
                        transition="transform 0.2s ease"
                    >
                        <HiChevronDown />
                    </Icon>
                </HStack>
            </Flex>

            {expanded && (
                <CompanyMembersTable
                    company={company}
                    onEditMember={onEditMember}
                    onToggleMember={onToggleMember}
                    onResendInvitation={onResendInvitation}
                    onAddMember={onAddMember}
                />
            )}
        </Box>
    );
};

export default CompanyRow;
